<template>
  <b-card
    v-if="data"
    class="financial-statistics-card"
    no-body
  >
    <b-card-header class="financial-statistics-header">
      <h4 class="chart-title mb-0">
        Financial Statistics
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
        >
          <feather-icon
            icon="ClockIcon"
            size="12"
            class="mr-1"
          />
          {{ formattedLastUpdated }}
        </small>
      </div>
    </b-card-header>

    <!-- Update Information Banner -->
    <div
      v-if="nextGeneratedAt"
      class="update-banner-container"
    >
      <div class="update-banner-wrapper">
        <div class="update-banner-body">
          <div class="info-icon-circle">
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="info-icon"
            />
          </div>
          <div class="update-banner-text">
            <span>
              Statistics are updated periodically. Recent transactions may be delayed by up to 1 hour and may not appear until the next update.
              <span
                v-if="nextUpdateTimeRemaining"
                class="next-update-text"
              >
                Next update in {{ nextUpdateTimeRemaining }}.
              </span>
            </span>
          </div>
        </div>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        class="update-now-btn"
        :disabled="isRefreshing"
        @click="handleRefresh"
      >
        <feather-icon
          :icon="isRefreshing ? 'LoaderIcon' : 'RefreshCwIcon'"
          size="14"
          :class="{ 'spin': isRefreshing }"
          class="mr-50"
        />
        Update Now
      </b-button>
    </div>

    <b-card-body class="tobe-widget-body">
      <b-row class="tobe-widget-grid">
        <b-col
          v-for="item in data"
          :key="item.icon"
          md="8"
          lg="6"
          xl="3"
          class="tobe-widget-col stat-col order-stat-col"
        >
          <div
            class="stat-box"
            :class="`stat-box-${item.color}`"
          >
            <div class="stat-box-content">
              <div class="stat-box-left">
                <div class="stat-value-spacer" />
                <h4
                  v-b-tooltip.hover.v-primary
                  class="stat-value"
                  :title="__numberWithCommas(item.title, true)"
                >
                  {{ __numberWithCommas(kFormatter(item.title)) }}
                </h4>
                <p class="stat-label">
                  {{ item.subtitle }}
                </p>
              </div>
              <div class="stat-box-right">
                <div
                  class="stat-icon-wrapper"
                  :class="`stat-icon-${item.color}`"
                >
                  <feather-icon
                    size="20"
                    :icon="item.icon"
                    class="stat-icon"
                  />
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { kFormatter } from '@core/utils/filter'
import moment from 'moment'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    nextGeneratedAt: {
      type: String,
      default: null,
    },
    themeColors: {
      type: Object,
      default: () => ({
        theme_color: '#7367f0',
      }),
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      updateInterval: null,
      currentTime: Date.now(),
      lastUpdateTime: Date.now(),
    }
  },
  computed: {
    nextUpdateTimeRemaining() {
      // Force reactivity by accessing both props and data
      const nextGen = this.nextGeneratedAt
      const current = this.currentTime

      if (!nextGen) return null
      try {
        // Parse the date - handle both UTC and local formats
        let nextUpdate
        if (nextGen.includes('T') || nextGen.includes('Z')) {
          nextUpdate = moment.utc(nextGen).local()
        } else {
          // Format: "2025-12-05 16:42:55"
          nextUpdate = moment.utc(nextGen, 'YYYY-MM-DD HH:mm:ss').local()
        }
        const now = moment(current)
        const diff = nextUpdate.diff(now)
        if (diff <= 0) return null

        const duration = moment.duration(diff)
        const hours = Math.floor(duration.asHours())
        const minutes = duration.minutes()

        if (hours > 0 && minutes > 0) {
          return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`
        }
        if (hours > 0) {
          return `${hours} hour${hours > 1 ? 's' : ''}`
        }
        if (minutes > 0) {
          return `${minutes} minute${minutes > 1 ? 's' : ''}`
        }
        return null
      } catch (e) {
        // Silently handle date parsing errors and return null
        return null
      }
    },
    formattedLastUpdated() {
      if (!this.lastUpdatedAt) return null

      try {
        // Parse UTC time from backend and convert to local time
        let lastUpdate
        const lastUpdated = this.lastUpdatedAt

        if (lastUpdated.includes('T') || lastUpdated.includes('Z')) {
          // ISO format with T or Z (e.g., "2025-01-15T10:30:00Z" or "2025-01-15T10:30:00.000Z")
          lastUpdate = moment.utc(lastUpdated).local()
        } else if (lastUpdated.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
          // Format: "2025-12-05 16:42:55" - assume UTC
          lastUpdate = moment.utc(lastUpdated, 'YYYY-MM-DD HH:mm:ss').local()
        } else {
          // Fallback to standard parsing
          lastUpdate = moment.utc(lastUpdated).local()
        }

        const now = moment(this.currentTime)
        const diff = now.diff(lastUpdate)

        if (diff < 0) return 'just now'

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
        if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
        if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`
        if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`
        return `${years} year${years !== 1 ? 's' : ''} ago`
      } catch (e) {
        // eslint-disable-next-line no-underscore-dangle
        return this.__dateTimeFormatterPassedTime(this.lastUpdatedAt)
      }
    },
  },
  watch: {
    nextGeneratedAt: {
      handler(newVal) {
        // Reset current time when nextGeneratedAt changes
        if (newVal) {
          this.currentTime = Date.now()
        }
      },
      immediate: true,
    },
    lastUpdatedAt: {
      handler() {
        // Update last update time when prop changes
        this.lastUpdateTime = Date.now()
      },
      immediate: true,
    },
  },
  mounted() {
    // Update countdown every second for real-time updates
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
      this.lastUpdateTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    kFormatter,
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    handleRefresh() {
      if (this.onRefresh) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.financial-statistics-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
}

.financial-statistics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #fff;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tobe-widget-body {
  padding: 1.5rem;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  font-family: 'Roboto', sans-serif;
}

.update-now-btn {
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 10px 16px;
  white-space: nowrap;
  flex-shrink: 0;
  height: auto;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ebf0ff !important;
  border: none !important;
  color: #3045c7 !important;
  outline: none !important;
  box-shadow: none !important;
}

.update-now-btn:hover {
  background-color: #e0e7ff !important;
  border: none !important;
  color: #3045c7 !important;
  outline: none !important;
  box-shadow: none !important;
}

.update-now-btn:focus {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.update-now-btn:active {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.update-now-btn:disabled {
  background-color: #ebf0ff !important;
  border: none !important;
  color: #94a3b8 !important;
  opacity: 0.6;
  outline: none !important;
  box-shadow: none !important;
}

.financial-statistics-body {
  padding: 0 1rem;
}

.financial-statistics-row {
  display: flex;
  align-items: stretch;
}

.match-height .stat-col {
  display: flex;
  flex-flow: column;
}

.match-height .stat-col > .stat-box {
  flex: 1 1 auto;
}

.stat-col {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.stat-box {
  border-radius: 1rem;
  padding: 1.5rem;
  min-height: 120px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  flex: 1 1 auto;
  width: 100%;
}

.stat-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
}

.stat-box-light-info {
  background: #e0f7fa;
}

.stat-box-light-warning {
  background: #fff3e0;
}

.stat-box-light-success {
  background: #e8f5e9;
}

.stat-box-light-primary {
  background: #e3f2fd;
}

.stat-box-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
  flex: 1;
}

.stat-box-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
}

.stat-value-spacer {
  flex: 1;
  min-height: 0;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 0.5rem;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-family: 'Roboto', sans-serif;
  align-self: flex-start;
}

.stat-label {
  font-size: 1.125rem;
  color: #6e6b7b;
  margin-bottom: 0;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  font-weight: 500;
  line-height: 1.4;
  font-family: 'Roboto', sans-serif;
  align-self: flex-start;
}

.stat-box-right {
  margin-left: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  align-self: flex-start;
}

.stat-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}

.stat-icon-light-info {
  background-color: #b2ebf2;
}

.stat-icon-light-warning {
  background-color: #ffe0b2;
}

.stat-icon-light-success {
  background-color: #c8e6c9;
}

.stat-icon-light-primary {
  background-color: #bbdefb;
}

.stat-icon {
  color: #5e5873;
}

.stat-icon-light-info .stat-icon {
  color: #00838f;
}

.stat-icon-light-warning .stat-icon {
  color: #e65100;
}

.stat-icon-light-success .stat-icon {
  color: #2e7d32;
}

.stat-icon-light-primary .stat-icon {
  color: #1565c0;
}

@media (max-width: 768px) {
  .financial-statistics-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: left;
  }
  .order-stat-col {
    flex: 0 0 auto;
    max-width: none;
  }
  .tobe-widget-col {
    padding: 0.5rem;
    display: flex;
  }

  .update-banner-container {
    padding: 0 1rem;
    flex-direction: column;
    align-items: stretch;
  }

  .update-banner-wrapper {
    width: 100%;
  }

  .update-banner-body {
    padding: 8px 12px;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .update-banner-text {
    flex: 1 1 auto;
    min-width: 0;
  }

  .update-now-btn {
    width: 100%;
    justify-content: center;
    padding: 8px 12px;
    align-self: stretch;
  }

  .stat-box {
    min-height: 100px;
    padding: 1rem;
  }

  .stat-value {
    font-size: 1.5rem;
  }
}

.update-banner-container {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 4px;
  padding: 0 1.5rem;
  flex-wrap: wrap;
}

.update-banner-wrapper {
  border: none;
  overflow: hidden;
  margin: 0;
  padding: 0;
  background: transparent;
  flex: 1;
  min-width: 0;
}

.update-banner-body {
  padding: 10px 16px;
  position: relative;
  margin: 0;
  background: #ebf0ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.update-banner-text {
  flex: 1;
  font-size: 0.9375rem;
  color: #42518f;
  line-height: 1.4;
  font-family: 'Roboto', sans-serif;
  min-width: 0;
}

.info-icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: none;
}

.info-icon {
  color: #42518f;
  width: 16px;
  height: 16px;
}

.next-update-text {
  font-weight: 600;
  color: #3045c7;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

</style>
